import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ApiError, api } from "../api";

export default function CampaignNew() {
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: "",
    from_local: "hello",
    from_name: "Austin",
    from_domain: "",
    reply_domain: "",
    mailing_address: "",
    subject_template: "Quick question about {{name}}",
    body_template: "Hi {{name}},\n\n[your pitch goes here]\n\nAustin",
    ai_persona: "Friendly, brief B2B salesperson.",
    ai_objective: "Get the recipient to agree to a 15-minute intro call this week.",
    ai_script: "If they ask price, dodge to value. If they say not interested, accept gracefully.",
    reply_policy: "always_draft" as "always_draft" | "auto_safe" | "full_auto",
    notify_webhook_url: "",
    success_webhook_url: "",
    daily_cap: 50,
    hourly_cap: 20,
    send_window_start_hour: 8,
    send_window_end_hour: 18,
    shadow_mode: true,
  });

  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => setForm({ ...form, [k]: v });

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (!form.mailing_address.trim()) {
      setErr("Mailing address is required (CAN-SPAM). Sends are blocked until you set it.");
      return;
    }
    setBusy(true);
    try {
      const r = await api<{ id: string; webhook_secret: string }>("/api/campaigns", {
        method: "POST",
        body: JSON.stringify({
          ...form,
          notify_webhook_url: form.notify_webhook_url || undefined,
          success_webhook_url: form.success_webhook_url || undefined,
          status: "draft",
        }),
      });
      nav(`/campaigns/${r.id}`);
    } catch (e) {
      setErr(e instanceof ApiError ? `${e.status} ${e.body.slice(0, 200)}` : (e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <h1 className="text-2xl font-semibold tracking-tight mb-6">New campaign</h1>
      {err && <div className="card border-red-200 bg-red-50 p-4 text-sm text-red-700 mb-4">{err}</div>}
      <form onSubmit={submit} className="space-y-6">
        <Section title="Identity">
          <Field label="Name (internal)">
            <input className="input" required value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Roofers - meeting Q2" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="From local part">
              <input className="input font-mono" value={form.from_local} onChange={(e) => set("from_local", e.target.value)} />
            </Field>
            <Field label="From display name">
              <input className="input" value={form.from_name} onChange={(e) => set("from_name", e.target.value)} />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Sending domain (override)">
              <input className="input font-mono" value={form.from_domain} onChange={(e) => set("from_domain", e.target.value)} placeholder="outreach.servicemomentum.com" />
            </Field>
            <Field label="Reply domain (override)">
              <input className="input font-mono" value={form.reply_domain} onChange={(e) => set("reply_domain", e.target.value)} placeholder="(matches sending if blank)" />
            </Field>
          </div>
          <p className="text-xs text-ink-500 -mt-2">
            Leave blank to use the worker defaults. Set per-campaign when you want to send from a different brand
            (e.g. <span className="font-mono">outreach.merchmakers.com</span> for one campaign,{" "}
            <span className="font-mono">outreach.servicemomentum.com</span> for another). Each domain must be verified in
            Cloudflare → Email → Email Sending first.
          </p>
        </Section>

        <Section title="Mailing address (CAN-SPAM, required)">
          <Field label="Physical mailing address — appears in the unsubscribe footer">
            <textarea
              className="input min-h-[60px]"
              required
              value={form.mailing_address}
              onChange={(e) => set("mailing_address", e.target.value)}
              placeholder={"Service Momentum\n123 Example Way Suite 4\nProvo, UT 84601"}
            />
          </Field>
          <p className="text-xs text-ink-500 -mt-2">
            Sends are blocked if this is empty. CAN-SPAM legally requires a real physical mailing address (P.O. boxes are
            allowed) in every commercial email.
          </p>
        </Section>

        <Section title="Initial outreach">
          <Field label="Subject template (use {{name}} for recipient name)">
            <input className="input" value={form.subject_template} onChange={(e) => set("subject_template", e.target.value)} />
          </Field>
          <Field label="Body template">
            <textarea
              className="input min-h-[180px] font-mono text-xs"
              value={form.body_template}
              onChange={(e) => set("body_template", e.target.value)}
            />
          </Field>
        </Section>

        <Section title="AI brain">
          <Field label="Persona">
            <textarea className="input min-h-[60px]" value={form.ai_persona} onChange={(e) => set("ai_persona", e.target.value)} />
          </Field>
          <Field label="Objective (1 sentence)">
            <input className="input" value={form.ai_objective} onChange={(e) => set("ai_objective", e.target.value)} />
          </Field>
          <Field label="Script / guidelines (long form — what to say in common scenarios)">
            <textarea className="input min-h-[100px]" value={form.ai_script} onChange={(e) => set("ai_script", e.target.value)} />
          </Field>
          <Field label="Reply policy">
            <select className="input" value={form.reply_policy} onChange={(e) => set("reply_policy", e.target.value as typeof form.reply_policy)}>
              <option value="always_draft">Always draft (you approve every send)</option>
              <option value="auto_safe">Auto-safe (auto-send low-risk, escalate the rest)</option>
              <option value="full_auto">Full auto (escalate only sensitive cases)</option>
            </select>
          </Field>
          <label className="flex items-start gap-2 text-sm">
            <input type="checkbox" checked={form.shadow_mode} onChange={(e) => set("shadow_mode", e.target.checked)} className="mt-0.5" />
            <span>
              <strong>Shadow mode</strong> for the first 50 replies — drafts but never auto-sends, regardless of policy. Recommended for new campaigns.
            </span>
          </label>
        </Section>

        <Section title="Webhooks (optional)">
          <Field label="Notify webhook URL — fired when a reply needs your attention">
            <input
              className="input font-mono text-xs"
              value={form.notify_webhook_url}
              onChange={(e) => set("notify_webhook_url", e.target.value)}
              placeholder="https://hooks.slack.com/services/..."
            />
          </Field>
          <Field label="Success webhook URL — fired when a goal is hit (e.g. meeting confirmed)">
            <input
              className="input font-mono text-xs"
              value={form.success_webhook_url}
              onChange={(e) => set("success_webhook_url", e.target.value)}
              placeholder="https://your.app/webhooks/aem-success"
            />
          </Field>
        </Section>

        <Section title="Sending caps">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Hourly cap">
              <input type="number" className="input" value={form.hourly_cap} onChange={(e) => set("hourly_cap", Number(e.target.value))} />
            </Field>
            <Field label="Daily cap">
              <input type="number" className="input" value={form.daily_cap} onChange={(e) => set("daily_cap", Number(e.target.value))} />
            </Field>
            <Field label="Window start hour (UTC)">
              <input type="number" min="0" max="23" className="input" value={form.send_window_start_hour} onChange={(e) => set("send_window_start_hour", Number(e.target.value))} />
            </Field>
            <Field label="Window end hour (UTC, exclusive)">
              <input type="number" min="1" max="24" className="input" value={form.send_window_end_hour} onChange={(e) => set("send_window_end_hour", Number(e.target.value))} />
            </Field>
          </div>
        </Section>

        <div className="flex justify-end gap-2">
          <button type="button" className="btn-secondary" onClick={() => nav("/campaigns")}>Cancel</button>
          <button type="submit" className="btn-primary" disabled={busy}>
            {busy ? "creating…" : "Create draft"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="card p-5 space-y-4">
      <h2 className="text-sm font-semibold tracking-tight text-ink-700">{title}</h2>
      {children}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="label">{label}</div>
      {children}
    </div>
  );
}
